import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { NavLink, useSearchParams } from "react-router";

import Avatar from "@/design/components/Avatar";
import HeaderCard from "@/design/components/HeaderCard";
import { type StoreState } from "@/store";
import formatDuration from "@/utils/formaty";
import Valkyrie, { viArrowRight, viArrowUpRightFromSquare, viList, viLocationPinDot } from "@sippy-platform/valkyrie";

import {
  alpha,
  Box,
  Button,
  Card,
  CardHeader,
  Checkbox,
  Dialog,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  useColorScheme,
  useTheme,
} from "@mui/material";
import {
  amber,
  blue,
  blueGrey,
  brown,
  cyan,
  deepOrange,
  deepPurple,
  green,
  grey,
  indigo,
  lightBlue,
  lightGreen,
  lime,
  orange,
  pink,
  purple,
  red,
  teal,
  yellow,
} from "@mui/material/colors";
import { defineChart } from "@tanstack/charts";
import { pie, polar, radialArc, radialText } from "@tanstack/charts/polar";
import { Chart } from "@tanstack/charts/react";
import { scaleLinear } from "@tanstack/charts/scales/linear";
import { tooltip } from "@tanstack/charts/tooltip";

import DataCell from "../_Components/DataCell";
import TableHead from "../_Components/TableHead";
import TableHeading from "../_Components/TableHeading";
import TableRow from "../_Components/TableRow";
import { ABANDONED, ANSWERED, ELSEWHERE, REJECTED, seriesOrder, useSeriesColors } from "./chartSeries";
import { SeriesToggle } from "./SeriesToggle";
import { StatCard } from "./StatCard";

const arcLabelStyle = { fill: "#000", fontSize: 14, fontWeight: 600 } as const;

const legendColors = [
  purple,
  cyan,
  lime,
  orange,
  pink,
  indigo,
  brown,
  teal,
  amber,
  deepPurple,
  lightBlue,
  lightGreen,
  blueGrey,
  deepOrange,
  red,
  blue,
  green,
  yellow,
  grey,
];

function polarScales() {
  return {
    angle: { scale: scaleLinear().domain([0, Math.PI * 2]) },
    radius: { scale: scaleLinear().domain([0, 1]) },
  };
}

export function AgentCard({
  agent,
  visibleSeries,
}: {
  agent: {
    agent: string;
    user: {
      attributes: {
        id: number;
        name: string;
        username?: string;
        exten: string;
      };
      type: "user" | "customDestination";
    };
    totals: {
      callers: number;
      rejected: number;
      abandoned: number;
      answered: number;
      elsewhere: number;
      waitTime: number;
      talkTime: number;
      ringTime: number;
    };
    queues: {
      queueName: string;
      queueId: number;
      rejected: number;
      callers: number;
      avgInQueue: number;
      answered: number;
      answeredElsewhere: number;
      ringTime: number;
      talkTime: number;
      waitTime: number;
      abandonedQueue: number;
    }[];
  };
  visibleSeries: string[];
}) {
  const seriesColors = useSeriesColors();
  const theme = useTheme();
  const { colorScheme } = useColorScheme();

  const [searchParams, setSearchParams] = useSearchParams();
  const currentSearchId = searchParams.get("id");
  const agentId = String(agent.agent);
  const isDialogOpen = currentSearchId === agentId;

  const linkSearchParams = useMemo(() => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("id", agentId);

    return nextSearchParams.toString();
  }, [searchParams, agentId]);

  const handleClose = () => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete("id");
    setSearchParams(nextSearchParams);
  };

  const pbx = useSelector((state: StoreState) => state.domain.pbx);

  const [queueFilter, setQueueFilter] = useState<number[]>([]);
  const [dialogSeries, setDialogSeries] = useState<string[]>(seriesOrder);
  const [hoveredTotalsSlice, setHoveredTotalsSlice] = useState<string | null>(null);
  const [hoveredQueueSlice, setHoveredQueueSlice] = useState<string | null>(null);

  const globalTotals = useMemo(() => {
    const globalTotals = {
      waitTime: 0,
      talkTime: 0,
      ringTime: 0,
      callers: 0,
    };

    agent.queues
      .filter((queue) => !queueFilter.includes(queue.queueId))
      .map((queue) => {
        globalTotals.callers += queue.callers;
        globalTotals.talkTime += queue.talkTime;
        globalTotals.waitTime += queue.waitTime;
        globalTotals.ringTime += queue.ringTime;
      });

    return globalTotals;
  }, [agent.queues, queueFilter]);

  const totalsChart = useMemo(() => {
    const slices = pie(
      [
        { id: ANSWERED, series: ANSWERED, value: agent.totals.answered },
        { id: ELSEWHERE, series: ELSEWHERE, value: agent.totals.elsewhere },
        { id: REJECTED, series: REJECTED, value: agent.totals.rejected },
        { id: ABANDONED, series: ABANDONED, value: agent.totals.abandoned },
      ].filter((slice) => visibleSeries.includes(slice.series) && slice.value !== 0),
      { value: "value", gapAngle: 0.05 },
    );
    const mutedSlices = hoveredTotalsSlice ? slices.filter((slice) => slice.id !== hoveredTotalsSlice) : [];
    const focusedSlices = hoveredTotalsSlice ? slices.filter((slice) => slice.id === hoveredTotalsSlice) : slices;

    return defineChart({
      scales: { x: null, y: null },
      marks: [
        polar({
          marks: [
            radialArc(mutedSlices, {
              color: "series",
              innerRadius: (layout) => layout.radius * 0.55,
              outerRadius: (layout) => layout.radius * 0.95,
              cornerRadius: 4,
              fillOpacity: 0.75,
              key: "id",
            }),
            radialArc(focusedSlices, {
              color: "series",
              innerRadius: (layout) => layout.radius * 0.5,
              outerRadius: (layout) => layout.radius,
              cornerRadius: 4,
              key: "id",
            }),
            radialText(slices, { angle: "angle", radius: 0.75, text: "value", ...arcLabelStyle }),
          ],
          scales: polarScales(),
        }),
      ],
      guides: false,
      focusRing: false,
      color: { domain: seriesOrder, range: seriesColors },
      theme: {
        foreground: colorScheme === "dark" ? "#fff" : theme.palette.text.primary,
        muted: colorScheme === "dark" ? "#fff" : theme.palette.text.secondary,
        grid: colorScheme === "dark" ? "#fff" : alpha(theme.palette.text.primary, 0.45),
      },
      tooltip: {
        use: tooltip,
        anchor: "pointer",
        content: (points) => ({
          rows: points.map((point) => ({
            label: String(point.datum.series),
            value: String(point.datum.value),
            color: point.color,
          })),
        }),
      },
    });
  }, [agent.totals, colorScheme, theme, seriesColors, visibleSeries, hoveredTotalsSlice]);

  const queueBreakdownChart = useMemo(() => {
    const queues = agent.queues.filter((queue) => !queueFilter.includes(queue.queueId));

    // Both rings sum to the same total, so their slices stay angularly aligned.
    const queueSlices = pie(
      queues.map((queue) => ({
        id: `queue-${queue.queueId}`,
        queueName: queue.queueName,
        value:
          (dialogSeries.includes(ANSWERED) ? queue.answered : 0) +
          (dialogSeries.includes(ELSEWHERE) ? queue.answeredElsewhere : 0) +
          (dialogSeries.includes(REJECTED) ? queue.rejected : 0) +
          (dialogSeries.includes(ABANDONED) ? queue.abandonedQueue : 0),
        color: legendColors[agent.queues.indexOf(queue) % legendColors.length][500],
      })),
      { value: "value", gapAngle: 0.05 },
    );

    const outcomeSlices = pie(
      queues.flatMap((queue) =>
        [
          { series: ANSWERED, value: queue.answered },
          { series: ELSEWHERE, value: queue.answeredElsewhere },
          { series: REJECTED, value: queue.rejected },
          { series: ABANDONED, value: queue.abandonedQueue },
        ]
          .filter((slice) => dialogSeries.includes(slice.series) && slice.value !== 0)
          .map((slice) => ({ ...slice, id: `${slice.series}-${queue.queueId}`, queueName: queue.queueName })),
      ),
      { value: "value", gapAngle: 0.05 },
    );
    const mutedQueueSlices = hoveredQueueSlice ? queueSlices.filter((slice) => slice.id !== hoveredQueueSlice) : [];
    const focusedQueueSlices = hoveredQueueSlice
      ? queueSlices.filter((slice) => slice.id === hoveredQueueSlice)
      : queueSlices;
    const mutedOutcomeSlices = hoveredQueueSlice ? outcomeSlices.filter((slice) => slice.id !== hoveredQueueSlice) : [];
    const focusedOutcomeSlices = hoveredQueueSlice
      ? outcomeSlices.filter((slice) => slice.id === hoveredQueueSlice)
      : outcomeSlices;

    return defineChart({
      scales: { x: null, y: null },
      marks: [
        polar({
          marks: [
            radialArc(mutedQueueSlices, {
              fill: (datum) => datum.color,
              innerRadius: (layout) => layout.radius * 0.31,
              outerRadius: (layout) => layout.radius * 0.51,
              cornerRadius: 4,
              fillOpacity: 0.25,
              key: "id",
            }),
            radialArc(focusedQueueSlices, {
              fill: (datum) => datum.color,
              innerRadius: (layout) => layout.radius * 0.26,
              outerRadius: (layout) => layout.radius * 0.56,
              cornerRadius: 4,
              key: "id",
            }),
            radialText(queueSlices, { angle: "angle", radius: 0.41, text: "value", ...arcLabelStyle }),
            radialArc(mutedOutcomeSlices, {
              color: "series",
              innerRadius: (layout) => layout.radius * 0.65,
              outerRadius: (layout) => layout.radius * 0.95,
              cornerRadius: 4,
              fillOpacity: 0.25,
              key: "id",
            }),
            radialArc(focusedOutcomeSlices, {
              color: "series",
              innerRadius: (layout) => layout.radius * 0.6,
              outerRadius: (layout) => layout.radius,
              cornerRadius: 4,
              key: "id",
            }),
            radialText(outcomeSlices, { angle: "angle", radius: 0.8, text: "value", ...arcLabelStyle }),
          ],
          scales: polarScales(),
        }),
      ],
      guides: false,
      focusRing: false,
      color: {
        domain: seriesOrder,
        range: seriesColors,
      },
      theme: {
        foreground: colorScheme === "dark" ? "#fff" : theme.palette.text.primary,
        muted: colorScheme === "dark" ? "#fff" : theme.palette.text.secondary,
        grid: colorScheme === "dark" ? "#fff" : alpha(theme.palette.text.primary, 0.45),
      },
      tooltip: {
        use: tooltip,
        anchor: "pointer",
        content: (points) => ({
          rows: points.map((point) => ({
            label:
              "series" in point.datum ? `${point.datum.series} in ${point.datum.queueName}` : point.datum.queueName,
            value: String(point.datum.value),
            color: point.color,
          })),
        }),
      },
    });
  }, [agent.queues, colorScheme, theme, dialogSeries, queueFilter, seriesColors, hoveredQueueSlice]);

  return (
    <Box sx={{ containerType: "inline-size" }}>
      <Card>
        <Box
          sx={{
            gap: "1px",
            bgcolor: "divider",
            display: "grid",
            gridTemplateColumns: { "@": "repeat(3, 1fr)", "@400": "144px 1fr" },
            gridTemplateRows: { "@": "repeat(3, auto)", "@400": "auto repeat(3, 1fr)" },
            gridTemplateAreas: {
              "@": "'list list list' 'talk wait ring' 'stats stats stats'",
              "@400": "'list list' 'talk stats' 'wait stats' 'ring stats'",
            },
          }}
        >
          <List sx={{ gridArea: "list", bgcolor: "background.card" }}>
            <ListItem
              sx={{ minHeight: 60 }}
              secondaryAction={
                <Button
                  component={NavLink}
                  color="inherit"
                  sx={{ fontSize: "1rem", px: 1.25 }}
                  to={`?${linkSearchParams}`}
                  id={`agentCard-${agent.agent}`}
                >
                  <Valkyrie icon={viArrowUpRightFromSquare} />
                </Button>
              }
            >
              {agent.user ? (
                <>
                  <ListItemAvatar>
                    <Avatar
                      text={agent.user.attributes.name}
                      icon={agent.user.type === "customDestination" ? viLocationPinDot : undefined}
                    />
                  </ListItemAvatar>
                  <ListItemText primary={agent.user.attributes.name} secondary={agent.user.attributes.exten} />
                </>
              ) : (
                <>
                  <ListItemText primary={agent.agent.split("-")[2]} secondary="Deleted" />
                </>
              )}
            </ListItem>
          </List>
          <StatCard title="Talk time" stat={formatDuration(agent.totals.talkTime)} sx={{ gridArea: "talk" }} />
          <StatCard title="Wait time" stat={formatDuration(agent.totals.waitTime)} sx={{ gridArea: "wait" }} />
          <StatCard title="Ring time" stat={formatDuration(agent.totals.ringTime)} sx={{ gridArea: "ring" }} />
          <Box
            sx={{
              gridArea: "stats",
              bgcolor: "background.card",
              containerType: "inline-size",
            }}
          >
            <Box
              sx={{
                height: "100%",
                display: "grid",
                gridTemplateColumns: {
                  "@": "auto",
                  "@250": "1fr 2fr",
                },
                gridTemplateRows: {
                  "@": "1fr 2fr",
                  "@250": "auto",
                },
                justifyContent: "space-around",
                alignItems: "center",
              }}
            >
              <StatCard stat={agent.totals.callers} title="Calls" sx={{ p: 0, flexGrow: 1 }} />
              <Box sx={{ py: 1.5 }}>
                <Chart
                  definition={totalsChart}
                  ariaLabel="Call outcomes for this agent"
                  height={168}
                  onFocusChange={(point) => setHoveredTotalsSlice(point?.datum.id ?? null)}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Card>
      <Dialog
        open={isDialogOpen}
        onClose={handleClose}
        maxWidth="lg"
        slotProps={{ paper: { id: "agentDetailsDialog" } }}
      >
        <HeaderCard>
          <CardHeader
            avatar={
              agent.user && (
                <Avatar
                  text={agent.user?.attributes.name ?? agent.agent}
                  icon={agent.user?.type === "customDestination" ? viLocationPinDot : undefined}
                />
              )
            }
            title={agent.user?.attributes.name ?? agent.agent.split("-")[2]}
            subheader={agent.user?.attributes.exten ?? "Deleted"}
            action={
              agent.user && (
                <Button
                  color="inherit"
                  endIcon={<Valkyrie icon={viArrowRight} />}
                  component={NavLink}
                  to={
                    agent.user.type === "user"
                      ? `/pbx/${pbx.context}/call-flow/users/${agent.user.attributes.id}`
                      : `/pbx/${pbx.context}/call-flow/custom-destinations/${agent.user.attributes.id}`
                  }
                >
                  {agent.user.type === "user" ? "User" : "Custom destination"}
                </Button>
              )
            }
          />
        </HeaderCard>
        <Stack
          sx={{
            gap: 2,
            mt: 2,
          }}
        >
          <Card elevation={1}>
            <Grid size={12} sx={{ containerType: "inline-size" }}>
              <Box
                sx={{
                  gap: "1px",
                  bgcolor: "divider",
                  display: "grid",
                  gridTemplateColumns: {
                    "@": "repeat(2, 1fr)",
                    "@520": "repeat(4, 1fr)",
                    "@700": "repeat(2, 160px) 1fr",
                  },
                  gridTemplateRows: {
                    "@": "repeat(2, 1fr) auto",
                    "@520": "repeat(2, auto)",
                  },
                  gridTemplateAreas: {
                    "@": "'talk wait' 'ring callers' 'stats stats'",
                    "@520": "'talk wait ring callers' 'stats stats stats stats'",
                    "@700": "'talk wait stats' 'ring callers stats'",
                  },
                }}
              >
                <StatCard title="Talk time" stat={formatDuration(globalTotals.talkTime)} sx={{ gridArea: "talk" }} />
                <StatCard title="Wait time" stat={formatDuration(globalTotals.waitTime)} sx={{ gridArea: "wait" }} />
                <StatCard title="Ring time" stat={formatDuration(globalTotals.ringTime)} sx={{ gridArea: "ring" }} />
                <StatCard title="Calls" stat={globalTotals.callers} sx={{ gridArea: "callers" }} />
                <Box
                  sx={{
                    gridArea: "stats",
                    bgcolor: "background.card",
                    display: "flex",
                    gap: 2,
                    p: 2,
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <SeriesToggle visibleSeries={dialogSeries} setVisibleSeries={setDialogSeries} />
                  <div className="flex flex-col items-center justify-center">
                    <Chart
                      definition={queueBreakdownChart}
                      ariaLabel="Call outcomes per queue for this agent"
                      height={240}
                      onFocusChange={(point) => setHoveredQueueSlice(point?.datum.id ?? null)}
                    />
                  </div>
                </Box>
              </Box>
            </Grid>
          </Card>
          <Card sx={{ overflowX: "auto" }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "min-content repeat(10, auto)",
                gridTemplateRows: "auto auto",
                px: 2,
              }}
            >
              <TableHead disableStyling>
                <TableHeading sx={{ py: 0 }}>
                  <Checkbox
                    sx={(theme) => ({
                      p: 0,
                      color: "#333",
                      ...theme.applyStyles("dark", {
                        color: "#fff",
                      }),
                      "&.Mui-checked": {
                        color: "#333",
                        ...theme.applyStyles("dark", {
                          color: "#fff",
                        }),
                      },
                    })}
                    disableRipple
                    onChange={(e) => setQueueFilter(e.target.checked ? [] : agent.queues.map((queue) => queue.queueId))}
                    checked={queueFilter.length === 0}
                    indeterminate={queueFilter.length !== agent.queues.length && queueFilter.length !== 0}
                  />
                </TableHeading>
                <TableHeading>Queue</TableHeading>
                <TableHeading align="right">Talk</TableHeading>
                <TableHeading align="right">Wait</TableHeading>
                <TableHeading align="right">Ring</TableHeading>
                <TableHeading align="right">Calls</TableHeading>
                <TableHeading align="right">Avg in queue</TableHeading>
                <TableHeading align="right">Answered</TableHeading>
                <TableHeading align="right">Answered elsewhere</TableHeading>
                <TableHeading align="right">Rejected</TableHeading>
                <TableHeading align="right">Abandoned</TableHeading>
              </TableHead>
              {agent.queues.map((queue, queueIndex) => (
                <TableRow key={queueIndex} sx={{ mx: -2 }}>
                  <Box>
                    <Checkbox
                      sx={{
                        p: 0,
                        color: legendColors[queueIndex % legendColors.length][500],
                        "&.Mui-checked": {
                          color: legendColors[queueIndex % legendColors.length][500],
                        },
                      }}
                      disableRipple
                      onChange={() =>
                        setQueueFilter((prev) =>
                          queueFilter.includes(queue.queueId)
                            ? prev.filter((item) => item !== queue.queueId)
                            : [...prev, queue.queueId],
                        )
                      }
                      checked={!queueFilter.includes(queue.queueId)}
                    />
                  </Box>
                  <DataCell
                    startDecorator={<Avatar text={queue.queueName} px={24} icon={viList} />}
                    primary={queue.queueName}
                  />
                  <DataCell tabular align="right" primary={formatDuration(queue.talkTime)} />
                  <DataCell tabular align="right" primary={formatDuration(queue.waitTime)} />
                  <DataCell tabular align="right" primary={formatDuration(queue.ringTime)} />
                  <DataCell tabular align="right" primary={queue.callers} />
                  <DataCell tabular align="right" primary={queue.avgInQueue} />
                  <DataCell tabular align="right" primary={queue.answered} />
                  <DataCell tabular align="right" primary={queue.answeredElsewhere} />
                  <DataCell tabular align="right" primary={queue.rejected} />
                  <DataCell tabular align="right" primary={queue.abandonedQueue} />
                </TableRow>
              ))}
              <TableRow sx={{ mx: -2 }}>
                <Box sx={{ height: 40 }} />
                <DataCell primary="Totals" />
                <DataCell tabular align="right" primary={formatDuration(agent.totals.talkTime)} />
                <DataCell tabular align="right" primary={formatDuration(agent.totals.waitTime)} />
                <DataCell tabular align="right" primary={formatDuration(agent.totals.ringTime)} />
                <DataCell tabular align="right" primary={agent.totals.callers} />
                <DataCell tabular align="right" primary="-" />
                <DataCell tabular align="right" primary={agent.totals.answered} />
                <DataCell tabular align="right" primary={agent.totals.elsewhere} />
                <DataCell tabular align="right" primary={agent.totals.rejected} />
                <DataCell tabular align="right" primary={agent.totals.abandoned} />
              </TableRow>
            </Box>
          </Card>
          <Stack
            direction="row"
            sx={{
              justifyContent: "flex-end",
              gap: 1,
              containerType: "inline-size",
            }}
          >
            <Button onClick={handleClose} variant="contained">
              Close
            </Button>
          </Stack>
        </Stack>
      </Dialog>
    </Box>
  );
}
